/**
 * Top-level React error boundary.
 *
 * Motivation:
 *   Without one, a single throwing render (e.g. `foo.toFixed(2)` when
 *   `foo` is null on a legacy backend doc, or a lazy-chunk import
 *   failing after a redeploy) unmounts the entire component tree.
 *   The user sees a black/blank page with no recovery path. That's
 *   the "black screen" complaint we saw in production.
 *
 * Behaviour:
 *   * On the first error inside the tree we render a friendly fallback
 *     panel with the error message, a "Reload" button, and — if the
 *     error looks like a chunk-load error — an auto-recover path that
 *     purges caches + unregisters the SW before reloading.
 *   * We also fire the same recovery from a top-level
 *     unhandledrejection handler, so async failures don't slip past
 *     the boundary undetected.
 *   * A ``sessionStorage`` flag makes the auto-recover a one-shot per
 *     tab so we can never loop.
 */
import React from "react";

const SELF_HEAL_FLAG = "refclick-self-healed";

function looksLikeChunkError(msg) {
  if (!msg) return false;
  const s = typeof msg === "string" ? msg : msg.message || String(msg);
  return /chunkloaderror|loading chunk|failed to fetch dynamically imported module|importing a module script failed/i.test(
    s,
  );
}

async function purgeAndReload() {
  try {
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
    if (navigator.serviceWorker) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
  } catch {
    /* swallow — reload anyway */
  }
  window.location.reload();
}

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[RefClick] render error captured by boundary:", error, info);
    // Self-heal once for chunk errors — the JS the browser has is
    // referring to a filename that no longer exists after a deploy.
    if (
      looksLikeChunkError(error) &&
      !sessionStorage.getItem(SELF_HEAL_FLAG)
    ) {
      sessionStorage.setItem(SELF_HEAL_FLAG, "boundary");
      purgeAndReload();
    }
  }

  render() {
    if (!this.state.error) return this.props.children;

    const msg = this.state.error.message || String(this.state.error);
    const isChunk = looksLikeChunkError(this.state.error);

    return (
      <div
        data-testid="app-error-boundary"
        style={{
          minHeight: "100vh",
          background: "#0d0d10",
          color: "#eaeaee",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
          fontFamily:
            "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Inter', system-ui, sans-serif",
        }}
      >
        <div
          style={{
            maxWidth: "480px",
            background: "rgba(255,255,255,0.03)",
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: "14px",
            padding: "24px",
          }}
        >
          <h1
            style={{
              margin: "0 0 8px",
              fontSize: "22px",
              fontWeight: 700,
              letterSpacing: "-0.02em",
            }}
          >
            RefClick hit an error
          </h1>
          <p
            style={{
              margin: "0 0 16px",
              fontSize: "14px",
              color: "#8a8a92",
              lineHeight: 1.5,
            }}
          >
            {isChunk
              ? "The app tried to load a stale asset. Refresh to pick up the latest build — your data is untouched."
              : "Something in the UI crashed while rendering — no data was touched. Refresh to reload the app."}
          </p>
          <pre
            data-testid="app-error-boundary-message"
            style={{
              margin: "0 0 16px",
              padding: "10px 12px",
              background: "rgba(0,0,0,0.35)",
              border: "1px solid rgba(255,255,255,0.06)",
              borderRadius: "8px",
              fontFamily:
                "'JetBrains Mono', ui-monospace, SFMono-Regular, monospace",
              fontSize: "12px",
              color: "#fca5a5",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              maxHeight: "160px",
              overflow: "auto",
            }}
          >
            {msg}
          </pre>
          <div style={{ display: "flex", gap: "8px" }}>
            <button
              data-testid="app-error-boundary-reload"
              onClick={() => window.location.reload()}
              style={{
                background: "#f59e0b",
                color: "#17130a",
                border: "1px solid #f59e0b",
                borderRadius: "8px",
                padding: "10px 16px",
                fontWeight: 700,
                fontSize: "13px",
                cursor: "pointer",
              }}
            >
              Reload
            </button>
            <button
              data-testid="app-error-boundary-purge"
              onClick={purgeAndReload}
              style={{
                background: "transparent",
                color: "#eaeaee",
                border: "1px solid rgba(255,255,255,0.15)",
                borderRadius: "8px",
                padding: "10px 16px",
                fontSize: "13px",
                cursor: "pointer",
              }}
            >
              Clear cache &amp; reload
            </button>
          </div>
        </div>
      </div>
    );
  }
}
